import React, { CSSProperties, useEffect, useState } from "react";
import { CatalogListingPage } from "./components/CatalogListingPage";
import { Subcategory, SubcategoryMenu } from "./components/SubcategoryMenu";
import { AboutUsPage } from "./components/AboutUsPage";
import { ContactPage } from "./components/ContactPage";
import { ShippingPolicyPage } from "./components/ShippingPolicyPage";
import { PrivacyPolicyPage } from "./components/PrivacyPolicyPage";
import { RefundPolicyPage } from "./components/RefundPolicyPage";
import { ShopAllCategories } from "./components/ShopAllCategories";
import { SiteHeader } from "./components/SiteHeader";
import { GlitterSparkles } from "./components/GlitterSparkles";
import { MobilePageTransition } from "./components/MobilePageTransition";
import { DesktopPageTransition } from "./components/DesktopPageTransition";
import { TermsPage } from "./components/TermsPage";

/** Routes that render their own page; any other path shows the home storefront. */
const namedRoutes = new Set(["/search", "/refund-policy", "/terms", "/privacy-policy", "/rhinestones", "/tumblers", "/shop-all", "/shipping-policy", "/about-us", "/contact"]);
type Hotspot = {
  label: string;
  path: string;
  desktop: CSSProperties;
  mobile: CSSProperties;
};
const desktopImage = "https://cdn.magicpatterns.com/uploads/hRhiM8mJ8dmNKpyk2MbmRM/Rhinestone_jars_poised_to_pour.png";
const mobileImage = "https://cdn.magicpatterns.com/uploads/8nGV6vT6SWCvoLhbCo8Zmz/file_00000000c87c81f799a6a05cfbdb9d01.png";
const desktopVideo = "https://cdn.magicpatterns.com/uploads/awc3rbsifiKXCwMzghyDMK/lv_0_20261010221156.mp4";
const mobileVideo = "https://cdn.magicpatterns.com/uploads/iLkbFj2xoLP6BvhUexh9Kf/lv_0_20261011103024.mp4";
const hotspots: Hotspot[] = [{
  label: "Shop all",
  path: "/shop-all",
  desktop: {
    left: "26%",
    top: "43.5%",
    width: "48%",
    height: "11%"
  },
  mobile: {
    left: "18%",
    top: "35%",
    width: "64%",
    height: "8%"
  }
}, {
  label: "Rhinestones",
  path: "/rhinestones",
  desktop: {
    left: "1%",
    top: "73.5%",
    width: "26%",
    height: "9%"
  },
  mobile: {
    left: "19%",
    top: "55.4%",
    width: "62%",
    height: "6%"
  }
}, {
  label: "Tumblers",
  path: "/tumblers",
  desktop: {
    left: "29%",
    top: "73.5%",
    width: "23.5%",
    height: "9%"
  },
  mobile: {
    left: "3%",
    top: "72.3%",
    width: "48%",
    height: "6.2%"
  }
}, {
  label: "Buckets",
  path: "/buckets",
  desktop: {
    left: "54.5%",
    top: "73.5%",
    width: "22.5%",
    height: "9%"
  },
  mobile: {
    left: "50%",
    top: "72.3%",
    width: "48%",
    height: "6.2%"
  }
}, {
  label: "Pens",
  path: "/pens",
  desktop: {
    left: "79%",
    top: "73.5%",
    width: "20.5%",
    height: "9%"
  },
  mobile: {
    left: "3%",
    top: "86.9%",
    width: "48%",
    height: "6%"
  }
}, {
  label: "Tools",
  path: "/tools",
  desktop: {
    left: "39%",
    top: "90%",
    width: "21%",
    height: "8.5%"
  },
  mobile: {
    left: "50%",
    top: "86.9%",
    width: "48%",
    height: "6%"
  }
}, {
  label: "About us",
  path: "/about-us",
  desktop: {
    left: "73%",
    top: "88%",
    width: "15.5%",
    height: "7%"
  },
  mobile: {
    left: "27%",
    top: "94.2%",
    width: "46%",
    height: "5%"
  }
}];
type CatalogPage = {
  title: string;
  description: string;
  keywords: string[];
  include?: string[];
  exclude?: string[];
  nameInclude?: string[];
  nameExclude?: string[];
  /** When false, every product shows as its own listing instead of being grouped by size. */
  groupVariations?: boolean;
  /** Shows every product that isn't in any other category. */
  catchAll?: boolean;
  parentPath?: string;
  backLabel?: string;
};
const rhinestoneSubcategories: Subcategory[] = [{
  label: "Glass Rhinestones",
  path: "/rhinestones/glass"
}, {
  label: "Silver-Back Rhinestones",
  path: "/rhinestones/silver-back"
}, {
  label: "Solid Rhinestones",
  path: "/rhinestones/solid"
}, {
  label: "Jelly Rhinestones",
  path: "/rhinestones/jelly"
}, {
  label: "Pearls",
  path: "/rhinestones/pearls"
}, {
  label: "Round Star Rhinestones",
  path: "/rhinestones/round-star"
}, {
  label: "Pastel Rhinestones",
  path: "/rhinestones/pastel"
}];
const tumblerSubsections: Subcategory[] = [{
  label: "Rhinestone Tumblers",
  path: "/tumblers/rhinestone"
}, {
  label: "20 oz Epoxy Tumblers",
  path: "/tumblers/20-oz-epoxy"
}, {
  label: "30 oz Epoxy Tumblers",
  path: "/tumblers/30-oz-epoxy"
}, {
  label: "40 oz Epoxy Tumblers",
  path: "/tumblers/40-oz-epoxy"
}];

/** Matches tumbler-style product names, including names that only state a cup size like "20 oz". */
const tumblerNamePattern = "tumbl|\\bskinny\\b|\\b\\d+\\s*-?\\s*oz\\b";

/** Matches any purse/bag-style product name so they all land in Rhinestone Purse and Backpacks. */
const bagNamePattern = "satchel|purse|backpack|hand\\s*bag|\\bbags?\\b|\\btotes?\\b|clutch|cross\\s*body|wristlet|wallet|\\bpouch(es)?\\b|fanny\\s*pack|belt\\s*bag|duffel|duffle|messenger|shoulder\\s*bag|\\bminaudi[eè]re";
function epoxyTumblerPage(size: number): CatalogPage {
  return {
    title: `${size} oz Epoxy Tumblers`,
    description: "Live Square catalog availability",
    keywords: [],
    nameInclude: [tumblerNamePattern],
    nameExclude: [bagNamePattern],
    include: [`^(?=[\\s\\S]*epoxy)(?=[\\s\\S]*\\b${size}\\s*-?\\s*oz)`],
    groupVariations: false,
    parentPath: "/tumblers",
    backLabel: "Back to tumblers"
  };
}

/** Product names that mean an item is decorated with rhinestones, not loose rhinestones. */
const decoratedItemPatterns = ["rhinestoned", "\\bpens?\\b", tumblerNamePattern, bagNamePattern, "bucket", "buddy", "hammer", "\\btools?\\b", "\\bkits?\\b", "kitty", "duck", "character", "figur", "\\bbanks?\\b", "keychain", "toilet"];

/**
 * Loose rhinestone pages. When `typeIsEnough` is true, the type word alone places a product here
 * (e.g. "Dark Amethyst Silverback SS16"), even if "rhinestone" isn't in its Square name.
 */
function looseRhinestonePage(title: string, typePattern: string, typeIsEnough = false): CatalogPage {
  return {
    title,
    description: "Loose rhinestones for your own projects",
    keywords: [],
    nameInclude: typeIsEnough ? [typePattern] : [`^(?=.*\\brhinestones?\\b)(?=.*${typePattern})`],
    nameExclude: decoratedItemPatterns,
    parentPath: "/rhinestones",
    backLabel: "Back to rhinestones"
  };
}
const catalogPages: Record<string, CatalogPage> = {
  "/catalog": {
    title: "Shop All",
    description: "Live product availability from Pickens Passion",
    keywords: []
  },
  "/buckets": {
    title: "Buckets",
    description: "Live Square catalog availability",
    keywords: ["bucket"],
    nameExclude: [bagNamePattern]
  },
  "/rhinestones/glass": looseRhinestonePage("Glass Rhinestones", "\\bglass\\b", true),
  "/rhinestones/silver-back": looseRhinestonePage("Silver-Back Rhinestones", "silver\\s*-?\\s*back", true),
  "/rhinestones/solid": looseRhinestonePage("Solid Rhinestones", "\\bsolid\\b"),
  "/rhinestones/jelly": looseRhinestonePage("Jelly Rhinestones", "\\bjelly\\b", true),
  "/rhinestones/round-star": looseRhinestonePage("Round Star Rhinestones", "\\bstars?\\b"),
  "/rhinestones/pastel": looseRhinestonePage("Pastel Rhinestones", "\\bpastels?\\b", true),
  "/rhinestones/pearls": {
    title: "Pearls",
    description: "Loose pearls for your own projects",
    keywords: [],
    nameInclude: ["\\bpearls?\\b"],
    nameExclude: decoratedItemPatterns,
    parentPath: "/rhinestones",
    backLabel: "Back to rhinestones"
  },
  "/satchels": {
    title: "Rhinestone Purse and Backpacks",
    description: "Live Square catalog availability",
    keywords: [],
    nameInclude: [bagNamePattern],
    groupVariations: false
  },
  "/resin-toilets": {
    title: "Resin Toilet",
    description: "Live Square catalog availability",
    keywords: [],
    nameInclude: ["toilet"],
    nameExclude: [bagNamePattern],
    groupVariations: false
  },
  "/rhinestone-characters": {
    title: "Rhinestone Characters",
    description: "Live Square catalog availability",
    // Rule: every character, figurine, and coin bank belongs here.
    keywords: ["character", "hello kitty", "duck", "figurine", "figure", "coin bank", "piggy bank", "money bank", "bank"],
    nameExclude: [bagNamePattern]
  },
  "/pens": {
    title: "Pens",
    description: "Live Square catalog availability",
    keywords: [],
    nameInclude: ["\\bpens?\\b"],
    nameExclude: [bagNamePattern],
    groupVariations: false
  },
  "/tools": {
    title: "Rhinestone Tools",
    description: "Live Square catalog availability",
    keywords: ["tool", "hammer"],
    nameExclude: [bagNamePattern],
    groupVariations: false
  },
  "/tumblers/rhinestone": {
    title: "Rhinestone Tumblers",
    description: "Live Square catalog availability",
    keywords: [],
    nameInclude: [tumblerNamePattern],
    nameExclude: [bagNamePattern],
    exclude: ["epoxy"],
    groupVariations: false,
    parentPath: "/tumblers",
    backLabel: "Back to tumblers"
  },
  "/tumblers/20-oz-epoxy": epoxyTumblerPage(20),
  "/tumblers/30-oz-epoxy": epoxyTumblerPage(30),
  "/tumblers/40-oz-epoxy": epoxyTumblerPage(40),
  "/all-that-sparkles": {
    title: "All That Sparkles",
    description: "Everything else that shines",
    keywords: [],
    // Rule: rhinestones of any kind never land here — they belong under Rhinestones.
    nameExclude: ["\\brhinestones?\\b", "silver\\s*-?\\s*back", "flat\\s*-?\\s*back", "\\bjelly\\b", "\\bpearls?\\b", "\\bpastels?\\b", "\\bglass\\b", "\\bss\\s*-?\\s*\\d+", "\\bgross\\b"],
    groupVariations: false,
    catchAll: true
  }
};

/**
 * Rule: any Square product that doesn't belong to one of the categories above lands in
 * All That Sparkles, so new inventory always has a home on the site.
 */
const allCategoryFilters = Object.entries(catalogPages)
// Resin Toilet no longer has a Shop All sign, so those items fall into All That Sparkles too.
.filter(([pagePath, page]) => pagePath !== "/catalog" && pagePath !== "/resin-toilets" && !page.catchAll).map(([, page]) => ({
  keywords: page.keywords,
  include: page.include,
  exclude: page.exclude,
  nameInclude: page.nameInclude,
  nameExclude: page.nameExclude
}));
function getPageTitle(path: string): string | null {
  return hotspots.find(hotspot => hotspot.path === path)?.label ?? null;
}
export function App() {
  const [showStorefront, setShowStorefront] = useState(false);
  const [path, setPath] = useState(() => window.location.pathname);
  const [searchQuery, setSearchQuery] = useState(() => new URLSearchParams(window.location.search).get("q") ?? "");
  const pageTitle = getPageTitle(path);
  const [leavingHome, setLeavingHome] = useState<"mobile" | "desktop" | null>(null);
  const isHome = !pageTitle && !catalogPages[path] && !namedRoutes.has(path);

  /**
   * Leaving the home page plays a transition video over the new page:
   * phones lift it up and away; on computers the new page simply appears when it ends.
   */
  const startHomeTransition = () => {
    if (!isHome || leavingHome) {
      return;
    }
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      return;
    }
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    setLeavingHome(isMobile ? "mobile" : "desktop");
  };
  const fromHome = <A extends unknown[],>(action: (...args: A) => void) => (...args: A) => {
    startHomeTransition();
    action(...args);
  };
  useEffect(() => {
    const handlePopState = () => {
      setPath(window.location.pathname);
      setSearchQuery(new URLSearchParams(window.location.search).get("q") ?? "");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // The browser tab always reads "Pickens Passion".
  useEffect(() => {
    document.title = "Pickens Passion";
  }, [path]);

  // Every page opens at the top, so customers always start from the beginning and scroll down.
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto"
    });
  }, [path, searchQuery]);
  useEffect(() => {
    if (pageTitle) {
      setShowStorefront(true);
      return undefined;
    }
    const timeout = window.setTimeout(() => setShowStorefront(true), 18000);
    return () => window.clearTimeout(timeout);
  }, [pageTitle]);
  const visitPage = (event: React.MouseEvent<HTMLAnchorElement>, nextPath: string) => {
    event.preventDefault();
    startHomeTransition();
    window.history.pushState({}, "", nextPath);
    setPath(nextPath);
  };
  const returnHome = () => {
    window.history.pushState({}, "", "/");
    setPath("/");
    setShowStorefront(true);
  };
  const openBuckets = () => {
    window.history.pushState({}, "", "/buckets");
    setPath("/buckets");
  };
  const openRhinestones = () => {
    window.history.pushState({}, "", "/rhinestones");
    setPath("/rhinestones");
  };
  const openPens = () => {
    window.history.pushState({}, "", "/pens");
    setPath("/pens");
  };
  const openTools = () => {
    window.history.pushState({}, "", "/tools");
    setPath("/tools");
  };
  const openRhinestoneCharacters = () => {
    window.history.pushState({}, "", "/rhinestone-characters");
    setPath("/rhinestone-characters");
  };
  const openShopAll = () => {
    window.history.pushState({}, "", "/shop-all");
    setPath("/shop-all");
  };
  const openShippingPolicy = () => {
    window.history.pushState({}, "", "/shipping-policy");
    setPath("/shipping-policy");
  };
  const openContact = () => {
    window.history.pushState({}, "", "/contact");
    setPath("/contact");
  };
  const openTerms = () => {
    window.history.pushState({}, "", "/terms");
    setPath("/terms");
  };
  const openPrivacyPolicy = () => {
    window.history.pushState({}, "", "/privacy-policy");
    setPath("/privacy-policy");
  };
  const openRefundPolicy = () => {
    window.history.pushState({}, "", "/refund-policy");
    setPath("/refund-policy");
  };
  const openSearch = (query: string) => {
    window.history.pushState({}, "", `/search?q=${encodeURIComponent(query)}`);
    setSearchQuery(query);
    setPath("/search");
  };
  const siteHeader = <>
      <MobilePageTransition active={leavingHome === "mobile"} onDone={() => setLeavingHome(null)} data-id="element-0" data-mp="qmgkgbpswwai" />
      <DesktopPageTransition active={leavingHome === "desktop"} onDone={() => setLeavingHome(null)} data-id="element-1" data-mp="vnns8ary1vh6" />
      <SiteHeader onHome={returnHome} onShopAll={fromHome(openShopAll)} onContact={fromHome(openContact)} onSearch={fromHome(openSearch)} data-id="element-2" data-mp="10kw8u6xg1rgb" />
    </>;
  const footerProps = {
    onOpenTerms: fromHome(openTerms),
    onOpenPrivacyPolicy: fromHome(openPrivacyPolicy),
    onOpenRefundPolicy: fromHome(openRefundPolicy),
    onOpenShippingPolicy: fromHome(openShippingPolicy)
  };
  if (path === "/search") {
    return <>
        {siteHeader}
        <CatalogListingPage key={searchQuery} title="Search results" description={searchQuery ? `Results for “${searchQuery}”` : "Type a product name to search"} query={searchQuery} searchBox={{
        initialQuery: searchQuery,
        onSearch: openSearch
      }} backLabel="Back to shop all" onBack={openShopAll} data-id="element-3" data-mp="1ob9unfpfqu2g" />
        <SiteFooter {...footerProps} data-id="element-4" data-mp="1oj3p9j6ofi3p" />
      </>;
  }
  const siteFooter = <SiteFooter {...footerProps} data-id="element-5" data-mp="1kh09iicz5rcm" />;
  if (path === "/refund-policy") {
    return <>
        {siteHeader}
        <RefundPolicyPage onBack={returnHome} onContact={openContact} data-id="element-6" data-mp="2h7srjljgxth1" />
        {siteFooter}
      </>;
  }
  if (path === "/terms") {
    return <>
        {siteHeader}
        <TermsPage onBack={returnHome} data-id="element-7" data-mp="27ebyijr5oief" />
        {siteFooter}
      </>;
  }
  if (path === "/privacy-policy") {
    return <>
        {siteHeader}
        <PrivacyPolicyPage onBack={returnHome} data-id="element-8" data-mp="2zwfv1zlzyasi" />
        {siteFooter}
      </>;
  }
  const navigateTo = (nextPath: string) => {
    window.history.pushState({}, "", nextPath);
    setPath(nextPath);
  };
  if (path === "/rhinestones") {
    return <>
        {siteHeader}
        <SubcategoryMenu title="Rhinestones" description="Choose a rhinestone type" subcategories={rhinestoneSubcategories} onOpen={navigateTo} onBack={returnHome} data-id="element-9" data-mp="y7jqmywfvjz0" />
        {siteFooter}
      </>;
  }
  if (path === "/tumblers") {
    return <>
        {siteHeader}
        <SubcategoryMenu title="Tumblers" description="Choose a tumbler style" subcategories={tumblerSubsections} onOpen={navigateTo} onBack={returnHome} data-id="element-10" data-mp="ahfpcy6q3wb6" />
        {siteFooter}
      </>;
  }
  const catalogPage = catalogPages[path];
  if (catalogPage) {
    return <>
        {siteHeader}
        <CatalogListingPage title={catalogPage.title} description={catalogPage.description} keywords={catalogPage.keywords} include={catalogPage.include} exclude={catalogPage.exclude} nameInclude={catalogPage.nameInclude} nameExclude={catalogPage.nameExclude} excludeCategories={catalogPage.catchAll ? allCategoryFilters : undefined} groupVariations={catalogPage.groupVariations} backLabel={catalogPage.backLabel} onBack={catalogPage.parentPath ? () => navigateTo(catalogPage.parentPath as string) : returnHome} data-id="element-11" data-mp="5i324cbxwn82" />
        {siteFooter}
      </>;
  }
  if (path === "/shop-all") {
    return <>
        {siteHeader}
        <ShopAllCategories onBack={returnHome} onOpenBuckets={openBuckets} onOpenPens={openPens} onOpenRhinestoneCharacters={openRhinestoneCharacters} onOpenRhinestones={openRhinestones} onOpenTools={openTools} onOpenTumblers={() => navigateTo("/tumblers")} onOpenSatchels={() => navigateTo("/satchels")} onOpenAllThatSparkles={() => navigateTo("/all-that-sparkles")} onSearch={openSearch} data-id="element-12" data-mp="6y779q6f03al" />
        {siteFooter}
      </>;
  }
  if (path === "/shipping-policy") {
    return <>
        {siteHeader}
        <ShippingPolicyPage onBack={returnHome} onOpenReturnPolicy={openRefundPolicy} data-id="element-13" data-mp="2drsp624v12n4" />
        {siteFooter}
      </>;
  }
  if (path === "/about-us") {
    return <>
        {siteHeader}
        <AboutUsPage onBack={returnHome} onShopAll={openShopAll} data-id="element-14" data-mp="1wtsnkhkf297j" />
        {siteFooter}
      </>;
  }
  if (path === "/contact") {
    return <>
        {siteHeader}
        <ContactPage onBack={returnHome} data-id="element-15" data-mp="26l5g7be1h3em" />
        {siteFooter}
      </>;
  }
  if (pageTitle) {
    return <>
        {siteHeader}
        <main className="relative flex min-h-[100dvh] w-full items-center justify-center overflow-hidden bg-black px-6 text-center text-white" data-id="element-16" data-mp="1dwrfuozh9uzw">
          <picture data-id="element-17" data-mp="1ivb10n68z2r7">
            <source media="(max-width: 767px)" srcSet={mobileImage} data-id="element-18" data-mp="1iy72glhb09ke" />
            <img src={desktopImage} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" data-id="element-19" data-mp="1nvytlda4jrnj" />
          </picture>
          <GlitterSparkles count={400} data-id="element-20" data-mp="2uddlrv5kfs39" />
          <div className="relative z-10" data-id="element-21" data-mp="2z9uihdqtzery">
            <h1 className="text-4xl font-semibold sm:text-6xl" data-id="element-22" data-mp="2z8syukvr1jym">{pageTitle}</h1>
            <p className="mt-4 text-xl sm:text-2xl" data-id="element-23" data-mp="nlz7r13zd4em">Coming soon</p>
            <button type="button" onClick={returnHome} className="mt-10 rounded-full border border-white/80 bg-black/50 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white" data-id="element-24" data-mp="io62caai4efe">
              Back to home
            </button>
          </div>
        </main>
        {siteFooter}
      </>;
  }
  return <>
      {siteHeader}
      <main className="min-h-[100dvh] w-full bg-black text-white" data-id="element-25" data-mp="3r3zi2mx0dlve">
        <section className="relative hidden min-h-[100dvh] w-full overflow-hidden md:block" aria-label="Pickens Passion storefront entrance" data-id="element-26" data-mp="3m5o7ub2sx9cn">
          <img src={desktopImage} alt="Pickens Passion custom tumblers, rhinestones, pens, buckets and crafting supplies" className="absolute inset-0 h-full w-full object-cover" data-id="element-27" data-mp="1pa9wrp4xlboe" />
          {!showStorefront && <div className="absolute inset-0 z-20 bg-black" data-id="element-28" data-mp="2789dgwd4o842">
              <video className="h-full w-full object-cover" autoPlay muted playsInline preload="auto" onEnded={() => setShowStorefront(true)} onError={() => setShowStorefront(true)} data-id="element-29" data-mp="278rjulti82pp">
                <source src={desktopVideo} type="video/mp4" data-id="element-30" data-mp="1xhenbgwrgodc" />
              </video>
              <button type="button" onClick={() => setShowStorefront(true)} className="absolute bottom-8 right-8 rounded-full border border-white/70 bg-black/45 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white opacity-70 transition-opacity duration-150 ease-out hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white" data-id="element-31" data-mp="1th4kue86gg38">
                Enter shop
              </button>
            </div>}
          {showStorefront && <StoreNav hotspots={hotspots} layout="desktop" onVisit={visitPage} data-id="element-32" data-mp="38y4oglmbuq03" />}
        </section>

        <section className="flex min-h-[100dvh] w-full items-center justify-center bg-black md:hidden" aria-label="Pickens Passion mobile storefront entrance" data-id="element-33" data-mp="3he3zhhjjh93i">
          <div className="relative w-full max-w-[940px] aspect-[940/1672] overflow-hidden" data-id="element-34" data-mp="3q6fg6boupi6a">
            <img src={mobileImage} alt="Pickens Passion custom tumblers, rhinestones, pens, buckets and crafting supplies" className="absolute inset-0 h-full w-full object-fill" data-id="element-35" data-mp="3v6jwa0lukavz" />
            {!showStorefront && <div className="absolute inset-0 z-20 bg-black" data-id="element-36" data-mp="32gl4vyk6k1o5">
                <video className="h-full w-full object-fill" autoPlay muted playsInline preload="auto" onEnded={() => setShowStorefront(true)} onError={() => setShowStorefront(true)} data-id="element-37" data-mp="37gfs9s4qy94g">
                  <source src={mobileVideo} type="video/mp4" data-id="element-38" data-mp="3cdg2mf8mpy1d" />
                </video>
                <button type="button" onClick={() => setShowStorefront(true)} className="absolute bottom-[3%] right-[5%] rounded-full border border-white/70 bg-black/45 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white opacity-80 transition-opacity duration-150 ease-out hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-id="element-39" data-mp="mpyw99b70s50">
                  Enter shop
                </button>
              </div>}
            {showStorefront && <StoreNav hotspots={hotspots} layout="mobile" onVisit={visitPage} data-id="element-40" data-mp="3ff9e3io8wouw" />}
          </div>
        </section>
      </main>
      {siteFooter}
    </>;
}
type SiteFooterProps = {
  onOpenTerms: () => void;
  onOpenPrivacyPolicy: () => void;
  onOpenRefundPolicy: () => void;
  onOpenShippingPolicy: () => void;
};
function SiteFooter({
  onOpenTerms,
  onOpenPrivacyPolicy,
  onOpenRefundPolicy,
  onOpenShippingPolicy
}: SiteFooterProps) {
  return <footer className="relative flex w-full flex-col items-center justify-between gap-1.5 bg-[#10070f] px-4 py-1.5 text-center sm:flex-row sm:gap-3 sm:px-8" data-id="element-41" data-mp="2wngkpn59l1mn">
      <GlitterSparkles count={120} data-id="element-42" data-mp="177ebl62opzje" />
      <p className="relative text-[10px] text-white/60 sm:text-left" data-id="element-43" data-mp="1774gx56p347d">Copyright © 2026 Pickens Passion - All Rights Reserved.</p>

      <div className="relative flex items-center justify-center gap-3" data-id="element-44" data-mp="xa9jkpm5r9uo">
        <button type="button" onClick={onOpenTerms} className="text-[10px] text-white/50 underline decoration-transparent underline-offset-2 transition-colors duration-150 ease-out hover:text-[#35dceb] hover:decoration-current focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35dceb]" data-id="element-45" data-mp="x86nbnuewkhi">
          Terms &amp; Conditions
        </button>
        <span className="text-[10px] text-white/30" aria-hidden="true" data-id="element-46" data-mp="1kzkf9sonie1y">
          |
        </span>
        <button type="button" onClick={onOpenPrivacyPolicy} className="text-[10px] text-white/50 underline decoration-transparent underline-offset-2 transition-colors duration-150 ease-out hover:text-[#35dceb] hover:decoration-current focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35dceb]" data-id="element-47" data-mp="5jbvjyuqkdfj">
          Privacy Policy
        </button>
        <span className="text-[10px] text-white/30" aria-hidden="true" data-id="element-48" data-mp="g18m5swruwgg">
          |
        </span>
        <button type="button" onClick={onOpenRefundPolicy} className="text-[10px] text-white/50 underline decoration-transparent underline-offset-2 transition-colors duration-150 ease-out hover:text-[#35dceb] hover:decoration-current focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35dceb]" data-id="element-49" data-mp="b3p2uel7vthn">
          Refund Policy
        </button>
        <span className="text-[10px] text-white/30" aria-hidden="true" data-id="element-50" data-mp="uqd6pgr1hcrq">
          |
        </span>
        <button type="button" onClick={onOpenShippingPolicy} className="text-[10px] text-white/50 underline decoration-transparent underline-offset-2 transition-colors duration-150 ease-out hover:text-[#35dceb] hover:decoration-current focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35dceb]" data-id="element-51" data-mp="3jkabtvdummkr">
          Shipping Policy
        </button>
      </div>

      <a href="https://www.websitesbymandy.com" target="_blank" rel="noreferrer" className="relative text-[10px] text-white/60 underline decoration-transparent underline-offset-2 transition-colors duration-150 ease-out hover:text-[#35dceb] hover:decoration-current focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35dceb] sm:text-right" data-id="element-52" data-mp="28qhr4dd5urjj">
        Made with a whole lot of grit by Trippin&apos; With Mandy™ LLC
      </a>
    </footer>;
}
type StoreNavProps = {
  hotspots: Hotspot[];
  layout: "desktop" | "mobile";
  onVisit: (event: React.MouseEvent<HTMLAnchorElement>, nextPath: string) => void;
};
function StoreNav({
  hotspots: items,
  layout,
  onVisit
}: StoreNavProps) {
  return <nav className="absolute inset-0 z-10" aria-label="Shop departments" data-id="element-53" data-mp="20ocpbrg32lzv">
      {items.map(hotspot => <a key={hotspot.label} href={hotspot.path} onClick={event => onVisit(event, hotspot.path)} style={layout === "mobile" ? hotspot.mobile : hotspot.desktop} className="group absolute block rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" aria-label={`Explore ${hotspot.label}`} title={`Explore ${hotspot.label}`} data-id="element-54" data-mp="1vpi2meezwdah">
          <span className="sr-only" data-id="element-55" data-mp="2scwh4613a05v">Explore {hotspot.label}</span>
          <span className="absolute inset-0 rounded-md border-2 border-transparent transition-colors duration-150 ease-out group-hover:border-white/90 group-focus-visible:border-white/90" aria-hidden="true" data-id="element-56" data-mp="2obksa2q1hiyq" />
        </a>)}
    </nav>;
}